/* TASK-217 (issue #335): decision behind components/PendingOAuthResume.jsx.

   GET /oauth-mcp/pending (mcp-worker, via the gateway's /oauth-mcp/* pass-
   through) answers { pending: false } or { pending: true, resumeUrl }. The app
   resumes ONLY when the server says so AND resumeUrl passes the same allowlist
   as ?next= (this environment's API origin, an /oauth-mcp/ path). The payload
   is data, never a navigation target on its own — a compromised or confused
   response can at most keep the user in the app. */
import { safeNext } from './safeNext.js';
import { freePreviewState } from './freePreview.js';
import { freePublishingState } from './freePublishing.js';

// Returns the validated URL to hand the browser to, or null to stay in the app.
export function shouldResume(payload, baseURL) {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.pending !== true) return null;
  if (typeof payload.resumeUrl !== 'string') return null;
  return safeNext(payload.resumeUrl, baseURL);
}

// Keep a truthful in-memory record even when the server response cannot be
// resumed. Unsafe URLs are discarded rather than copied into app state; the
// banner can still explain that the request must be restarted.
export function pendingOAuthSnapshot(payload, userId, baseURL) {
  if (!payload || typeof payload !== 'object' || payload.pending !== true
    || typeof userId !== 'string' || !userId
    || typeof payload.ownerUserId !== 'string' || !payload.ownerUserId
    || payload.ownerUserId !== userId) return null;
  const expiresAt = typeof payload.expiresAt === 'string' && Number.isFinite(Date.parse(payload.expiresAt))
    ? payload.expiresAt
    : null;
  const connector = ['chatgpt', 'claude'].includes(payload.connector) ? payload.connector : null;
  return { resumeUrl: shouldResume(payload, baseURL), expiresAt, userId,
    ...(connector ? { connector, canContinueWithPersonalWorkspace: payload.canContinueWithPersonalWorkspace === true } : {}),
  };
}

const CONSENT_ATTEMPT_KEY_PREFIX = 'dathent.pendingOAuth.consentAttempt';
const BANNER_DISMISSED_KEY_PREFIX = 'dathent.pendingOAuth.bannerDismissed';

// A parked authorize request is only usable while its server-provided expiry is
// still in the future. Keep this check next to shouldResume/safeNext so both the
// automatic hand-off and the manual recovery action apply the same rules.
export function validatedPendingConsent(pending, { userId, baseURL, now = Date.now() } = {}) {
  if (!pending || pending.userId !== userId) return null;
  const resumeUrl = safeNext(pending.resumeUrl, baseURL);
  const expiresAt = typeof pending.expiresAt === 'string' ? Date.parse(pending.expiresAt) : NaN;
  if (!resumeUrl || !Number.isFinite(expiresAt) || expiresAt <= now) return null;
  return resumeUrl;
}

// The pending endpoint currently exposes no opaque authorization id. Persist a
// SHA-256 identity instead: it is stable across reloads and expiry renewals,
// separates users/authorize queries, and never writes OAuth state/PKCE query
// data to storage.
async function pendingRequestStorageKey(prefix, pending, userId, subtle, baseURL) {
  if (!subtle || typeof TextEncoder === 'undefined' || userId == null) return null;
  const resumeUrl = safeNext(pending?.resumeUrl, baseURL);
  if (!resumeUrl || pending?.userId !== userId) return null;
  // Expiry is deliberately excluded. Re-parking the same authorize request can
  // refresh its TTL, but must not bypass the already-attempted marker.
  const input = `${String(userId)}\0${resumeUrl}`;
  const digest = await subtle.digest('SHA-256', new TextEncoder().encode(input));
  const opaqueId = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${prefix}.${opaqueId}`;
}

export function consentAttemptStorageKey(pending, userId, subtle = globalThis.crypto?.subtle, baseURL) {
  return pendingRequestStorageKey(CONSENT_ATTEMPT_KEY_PREFIX, pending, userId, subtle, baseURL);
}

export function bannerDismissalStorageKey(pending, userId, subtle = globalThis.crypto?.subtle, baseURL) {
  return pendingRequestStorageKey(BANNER_DISMISSED_KEY_PREFIX, pending, userId, subtle, baseURL);
}

function samePendingRequest(original, latest, userId, baseURL) {
  if (original?.userId !== userId || latest?.userId !== userId) return false;
  const originalUrl = safeNext(original.resumeUrl, baseURL);
  const latestUrl = safeNext(latest.resumeUrl, baseURL);
  return Boolean(originalUrl && latestUrl && originalUrl === latestUrl);
}

// Record-before-navigation is intentional. If storage or hashing is unavailable
// we do not risk an automatic reload loop; the banner leaves a manual action.
export async function attemptAutomaticConsent({
  pending,
  userId,
  billingReal,
  checkoutChoiceDeferred,
  storage,
  navigate,
  getPendingOAuth,
  baseURL,
  subtle,
  locks,
  visible = true,
  now = () => Date.now(),
  isCurrent = () => true,
}) {
  const target = validatedPendingConsent(pending, { userId, baseURL, now: now() });
  if (!billingReal || checkoutChoiceDeferred || !target || !visible) return { status: 'blocked' };

  if (!storage || typeof getPendingOAuth !== 'function' || typeof locks?.request !== 'function') {
    return { status: 'manual', pending: { ...pending, automatic: false } };
  }

  let key;
  try {
    key = await consentAttemptStorageKey(pending, userId, subtle, baseURL);
  } catch {
    return { status: 'manual', pending: { ...pending, automatic: false } };
  }
  if (!key || !isCurrent()) {
    return { status: key ? 'stale' : 'manual', pending: key ? pending : { ...pending, automatic: false } };
  }

  const transition = async () => {
    let payload;
    try {
      payload = await getPendingOAuth();
    } catch {
      return { status: 'manual', pending: { ...pending, automatic: false } };
    }
    if (!isCurrent()) return { status: 'stale', pending };

    if (payload && typeof payload === 'object' && payload.pending === false) {
      return { status: 'idle', pending: null };
    }

    const latest = pendingOAuthSnapshot(payload, userId, baseURL);
    if (!latest) {
      return { status: 'idle', pending: null };
    }
    if (!samePendingRequest(pending, latest, userId, baseURL)) {
      return { status: 'changed', pending: { ...latest, automatic: false } };
    }

    // The endpoint is no-cache. Validate its latest expiry immediately before
    // claiming the opaque cross-tab marker and navigating.
    const freshTarget = validatedPendingConsent(latest, { userId, baseURL, now: now() });
    if (!freshTarget || freshTarget !== target || !isCurrent()) {
      return { status: 'invalid', pending: { ...latest, automatic: false } };
    }

    try {
      if (storage.getItem(key) === '1') {
        return { status: 'attempted', pending: { ...latest, automatic: false } };
      }
      storage.setItem(key, '1');
      if (storage.getItem(key) !== '1') {
        return { status: 'manual', pending: { ...latest, automatic: false } };
      }
    } catch {
      return { status: 'manual', pending: { ...latest, automatic: false } };
    }

    // No await between the final lifecycle check, marker, and hand-off.
    if (!isCurrent()) {
      try { storage.removeItem?.(key); } catch { /* a stale marker only suppresses auto */ }
      return { status: 'stale', pending };
    }
    try {
      navigate(freshTarget);
      return { status: 'navigated', pending: latest };
    } catch {
      // The marker remains truthful when navigation itself throws and prevents
      // an automatic loop; the visible manual action can perform a fresh retry.
      return { status: 'attempted', pending: { ...latest, automatic: false } };
    }
  };

  try {
    return await locks.request(`${key}.lock`, { mode: 'exclusive' }, transition);
  } catch {
    return { status: 'manual', pending: { ...pending, automatic: false } };
  }
}

const REAL_BILLING_STATUSES = new Set(['active', 'trialing', 'checkout_completed']);

// TASK-331: accessState is the single derived field computed server-side by
// @dathent-sro/entitlements (readLatestSubscription / getOrganizationBillingState)
// and exposed on GET /api/billing/entitlements. It supersedes ad-hoc
// billingStatus-set matching — see the brief's I1 invariant ("accessState is
// computed in exactly one function; no consumer re-derives it from raw status
// strings"). Every state here except 'new' represents a genuine (or exempt)
// billing relationship for the ChatGPT hand-back decision: a lapsed customer
// with subscription history is not the same "no billing at all" case as a
// brand-new signup, so it counts as real too (unlike the old REAL_BILLING_STATUSES,
// which had no slot for it).
// TASK-331 phase 2 (Subscription Schedules): 'downgrade_scheduled' is a live
// paying subscriber on their CURRENT (higher) plan — a Stripe Subscription
// Schedule just means a LOWER plan takes over later — so it belongs here for
// exactly the same reason 'active'/'cancel_scheduled' do.
const REAL_ACCESS_STATES = new Set(['trialing', 'active', 'grace', 'cancel_scheduled', 'downgrade_scheduled', 'lapsed', 'free']);

// Card-backed billing only. Payment trouble and preview-mode access are
// deliberately not real billing for the ChatGPT hand-back decision.
// Fail-soft: an old backend that hasn't shipped accessState yet falls straight
// through to the original REAL_BILLING_STATUSES check — byte-identical.
export function billingIsReal(entitlements) {
  if (entitlements?.billingGateExempt === true) return true;
  if (typeof entitlements?.accessState === 'string') {
    return REAL_ACCESS_STATES.has(entitlements.accessState);
  }
  return REAL_BILLING_STATUSES.has(entitlements?.billingStatus);
}

// Pure client card-gate decision. `previewMode` is accepted deliberately so
// callers/tests make the old bypass dimension explicit: preview changes the
// onboarding and timed-overlay UX, but it never substitutes for real billing.
//
// TASK-331 (A2/A4): the card wall (PlanSelectScreen / TrialCardCaptureContainer)
// is reserved for accessState === 'new' — a brand-new signup with no
// subscription history. Every other accessState (trialing/active/grace/
// cancel_scheduled/lapsed/free) allows straight through: a lapsed customer
// reads their whole workspace and is blocked write-by-write with PLAN_LAPSED
// instead of being walled behind the free-trial card capture again. Fail-soft:
// an old backend without accessState keeps the exact pre-TASK-331 decision.
//
// GH-1133 S5 (free preview before the card, design §3.9 item 1): a 'new'
// account whose entitlements carry the gateway's freePreview block (granted
// preview arm: active, read-only rollback, or pending email verification)
// gets 'preview' — the app opens on the free-preview board instead of the
// wall — but ONLY when the caller passes `freePreview: true` (App.jsx passes
// the client switch, which can only force 'gate'). `hidden` (hide_ui=1), an
// inactive block, no block (control/NULL/denied) or the switch off → today's
// 'gate', byte for byte.
export function cardGateDecision(entitlements, { previewMode = false, freePreview = false } = {}) {
  void previewMode;
  if (freePublishingState(entitlements)) {
    const preview = freePreview && freePreviewState(entitlements);
    return preview && preview.mode !== 'hidden' ? 'preview' : 'allow';
  }
  if (typeof entitlements?.accessState === 'string') {
    if (freePreview === true) {
      const state = freePreviewState(entitlements);
      if (state && state.mode !== 'hidden') return 'preview';
    }
    if (entitlements.accessState !== 'new') return 'allow';
    return 'gate';
  }
  return billingIsReal(entitlements) || Boolean(entitlements?.paymentIssue)
    ? 'allow'
    : 'gate';
}

// Owner bug: on /onboarding (and any other non-/app screen with its own
// fixed/sticky header), PendingOAuthBanner's `floating` instance is
// `position: fixed` and can render on top of that header — on mobile it
// covered the onboarding header's Skip button entirely. Rather than hardcode
// a pixel guess (the banner wraps to a second line for the long "waiting...
// available until <date>" copy on narrow phones, and text length varies by
// locale), PendingOAuthBanner measures its own rendered DOMRect and calls
// this to publish a CSS var (--pending-oauth-banner-h) that any screen's
// header can add as a top offset. Pure + independently testable without a
// DOM: `rect` only needs a numeric `.bottom` (a real DOMRect has one; so
// does a plain {bottom: N} in a test).
export function computeBannerOffsetPx(rect, gap = 8) {
  if (!rect || !Number.isFinite(rect.bottom)) return 0;
  return Math.max(0, Math.ceil(rect.bottom + gap));
}
